import { useMemo, useRef, useState } from 'react'
import { EMOJI_CATEGORIES, recentEmoji, rememberEmoji, searchEmoji } from '../emoji'
import { useCustomEmoji } from '../data/hooks'
import { Emoji, Popover, type Anchor } from './common'

const CATEGORY_ICONS: Record<string, string> = {
  Recent: 'clock4',
  'Smileys & Emotion': 'grinning',
  'People & Body': 'wave',
  'Animals & Nature': 'dog',
  'Food & Drink': 'pizza',
  Activities: 'soccer',
  'Travel & Places': 'airplane',
  Objects: 'bulb',
  Symbols: 'heart',
  Flags: 'checkered_flag',
  Custom: 'star',
}

export function EmojiPicker({ anchor, onSelect, onClose, placement = 'above' }: { anchor: Anchor; onSelect: (name: string) => void; onClose: () => void; placement?: 'above' | 'below' }) {
  const [query, setQuery] = useState('')
  const [hover, setHover] = useState<string | null>(null)
  const { data: custom } = useCustomEmoji()
  const scroller = useRef<HTMLDivElement>(null)

  const sections = useMemo(() => {
    const recent = recentEmoji()
    const customNames = Object.keys(custom ?? {}).sort()
    return [
      ...(recent.length ? [{ name: 'Recent', emojis: recent }] : []),
      ...EMOJI_CATEGORIES,
      ...(customNames.length ? [{ name: 'Custom', emojis: customNames }] : []),
    ]
  }, [custom])

  const results = useMemo(() => (query.trim() ? searchEmoji(query.trim().replace(/:/g, ''), custom, 120) : null), [query, custom])

  const pick = (name: string) => {
    rememberEmoji(name)
    onSelect(name)
    onClose()
  }

  const grid = (names: string[]) => (
    <div className="emoji-grid">
      {names.map((n) => (
        <button key={n} type="button" className="emoji-cell" onClick={() => pick(n)} onMouseEnter={() => setHover(n)} aria-label={n}>
          <Emoji name={n} />
        </button>
      ))}
    </div>
  )

  return (
    <Popover anchor={anchor} onClose={onClose} placement={placement} className="emoji-picker">
      <input
        autoFocus
        className="emoji-search"
        placeholder="Search emoji"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && results?.[0]) pick(results[0])
        }}
      />
      {!results && (
        <nav className="emoji-tabs">
          {sections.map((s) => (
            <button
              key={s.name}
              type="button"
              title={s.name}
              onClick={() => scroller.current?.querySelector(`[data-section="${s.name}"]`)?.scrollIntoView({ block: 'start' })}
            >
              <Emoji name={CATEGORY_ICONS[s.name] ?? 'grey_question'} />
            </button>
          ))}
        </nav>
      )}
      <div className="emoji-scroll" ref={scroller}>
        {results ? (
          results.length ? grid(results) : <p className="muted pad">No emoji found</p>
        ) : (
          sections.map((s) => (
            <section key={s.name} data-section={s.name}>
              <h4>{s.name}</h4>
              {grid(s.emojis)}
            </section>
          ))
        )}
      </div>
      <footer className="emoji-preview">{hover ? <><Emoji name={hover} size={22} /> <span>:{hover}:</span></> : <span className="muted">Pick an emoji</span>}</footer>
    </Popover>
  )
}
